import React, { useRef, useState } from 'react';
import Button from './Button';
import StepIndicator from './StepIndicator';
import {
  RUBRIC_UPLOAD_ACCEPT,
  convertRubricFilesToCriteria,
  StructuredRubricResult
} from '../services/rubricImportService';
import { RubricTransformContext } from '../types';

interface RubricUploadAssistantProps {
  totalMarks: number | '';
  subject: string;
  yearLevel: string;
  topic: string;
  assessmentType: string;
  isDisabled?: boolean;
  onRubricReady: (result: StructuredRubricResult) => void;
}

const RubricUploadAssistant: React.FC<RubricUploadAssistantProps> = ({
  totalMarks,
  subject,
  yearLevel,
  topic,
  assessmentType,
  isDisabled = false,
  onRubricReady
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [phase, setPhase] = useState<'idle' | 'extracting' | 'success' | 'error'>('idle');
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingResult, setPendingResult] = useState<StructuredRubricResult | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);

  const handleBrowseClick = () => {
    if (!isDisabled) {
      fileInputRef.current?.click();
    }
  };

  const handleCameraClick = () => {
    if (!isDisabled) {
      cameraInputRef.current?.click();
    }
  };

  const applySelectedFiles = (files: FileList | null) => {
    if (!files) return;
    const fileArray = Array.from(files);
    setSelectedFiles(fileArray);
    setPendingResult(null);
    setPhase('idle');
    setHasApplied(false);
    setStatusMessage(fileArray.length ? `${fileArray.length} file${fileArray.length > 1 ? 's' : ''} ready.` : '');
  };

  const handleFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    applySelectedFiles(event.target.files);
    event.target.value = '';
  };

  const handleCameraSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    applySelectedFiles(event.target.files);
    event.target.value = '';
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleProcess = async () => {
    if (!totalMarks || Number(totalMarks) <= 0) {
      setPhase('error');
      setStatusMessage('Enter the total marks for this assessment before converting a rubric.');
      return;
    }

    if (selectedFiles.length === 0) {
      setPhase('error');
      setStatusMessage('Add at least one PDF, DOCX, or image of your rubric.');
      return;
    }

    const context: RubricTransformContext = {
      totalMarks: Number(totalMarks),
      subject,
      yearLevel,
      topic,
      assessmentType,
    };

    try {
      setHasApplied(false);
      setIsProcessing(true);
      setPhase('extracting');
      setStatusMessage('Extracting text from your rubric...');
      const result = await convertRubricFilesToCriteria(selectedFiles, context, {
        onStatus: (msg: string) => setStatusMessage(msg),
      });
      setPendingResult(result);
      setPhase('success');
      setStatusMessage('Rubric converted. Review the summary then click "Use This Rubric" to fill the form.');
    } catch (error) {
      console.error('Rubric conversion failed:', error);
      setPendingResult(null);
      setPhase('error');
      setStatusMessage(error instanceof Error ? error.message : 'Failed to convert rubric. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApply = () => {
    if (!pendingResult) return;
    onRubricReady(pendingResult);
    setHasApplied(true);
    setStatusMessage('Structured rubric applied below. Please review each criterion before saving.');
  };

  const clearSelection = () => {
    setSelectedFiles([]);
    setPendingResult(null);
    setStatusMessage('');
    setPhase('idle');
    setHasApplied(false);
  };

  const rubricSteps = [
    { id: 'add', name: 'Add files' },
    { id: 'convert', name: 'Convert' },
    { id: 'review', name: 'Review extract' },
    { id: 'apply', name: 'Apply' },
  ];

  const currentRubricStep =
    hasApplied ? 'apply'
    : pendingResult ? 'review'
    : phase === 'extracting' || isProcessing ? 'convert'
    : selectedFiles.length > 0 ? 'convert'
    : 'add';

  return (
    <div className="rounded-2xl border border-primary-100 bg-primary-50/40 p-5 space-y-4">
      <div className="rounded-xl bg-white/80 border border-primary-100 px-3 sm:px-4 py-3 shadow-inner">
        <StepIndicator
          steps={rubricSteps}
          currentStep={currentRubricStep}
          compact
        />
      </div>
      <div className="flex flex-wrap justify-between gap-3">
        <div>
          <p className="text-base font-semibold text-primary-900">Upload an existing rubric</p>
          <p className="text-sm text-secondary-700">
            Accepts PDF, DOCX, or image files (JPEG/PNG/WEBP/HEIC) up to 12MB each. We&apos;ll extract the text and
            map each criterion into the structured fields below. Add files or use your camera to snap a rubric photo.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" size="sm" onClick={clearSelection} disabled={isDisabled || (!selectedFiles.length && !pendingResult)}>
            Clear
          </Button>
          <Button variant="primary" size="sm" onClick={handleBrowseClick} disabled={isDisabled}>
            Add Files
          </Button>
          <span className="text-xs font-semibold text-secondary-500">OR</span>
          <Button variant="secondary" size="sm" onClick={handleCameraClick} disabled={isDisabled}>
            Use Camera
          </Button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={RUBRIC_UPLOAD_ACCEPT}
        multiple
        className="hidden"
        onChange={handleFilesSelected}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={handleCameraSelected}
      />

      {selectedFiles.length > 0 ? (
        <div className="space-y-2">
          {selectedFiles.map((file, idx) => (
            <div
              key={`${file.name}-${idx}`}
              className="flex items-center justify-between rounded-xl border border-secondary-200 bg-white px-4 py-2 text-sm"
            >
              <span className="truncate max-w-[70%] text-secondary-800">{file.name}</span>
              <button
                type="button"
                className="text-xs font-semibold text-primary-700 hover:text-primary-900"
                onClick={() => removeFile(idx)}
                disabled={isProcessing || isDisabled}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-secondary-600">No files or photos yet. Add files or use your camera to get started.</p>
      )}

      <div className="flex flex-wrap gap-3">
        <Button
          variant="secondary"
          onClick={handleProcess}
          isLoading={isProcessing}
          disabled={isDisabled || isProcessing}
          loadingText="Converting rubric..."
        >
          Convert &amp; Stage Rubric
        </Button>
        {pendingResult && (
          <Button
            variant="primary"
            onClick={handleApply}
            disabled={isDisabled || isProcessing}
          >
            Use This Rubric
          </Button>
        )}
      </div>

      {statusMessage && (
        <div
          className={`rounded-xl border px-4 py-3 text-sm ${
            phase === 'error'
              ? 'border-red-200 bg-red-50 text-red-700'
              : phase === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-primary-100 bg-white text-secondary-700'
          }`}
        >
          {statusMessage}
        </div>
      )}

      {pendingResult && (
        <div className="rounded-2xl border border-secondary-200 bg-white p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-secondary-900">
                Ready to apply &middot; {pendingResult.criteria.length} criteria detected
              </p>
              <p className="text-sm text-secondary-600">
                Files: {pendingResult.fileSummaries.map(file => file.name).join(', ')}
              </p>
            </div>
          </div>

          {pendingResult.summary.length > 0 && (
            <div className="text-sm text-secondary-700 space-y-1">
              {pendingResult.summary.map((line, idx) => (
                <p key={`summary-${idx}`}>&bull; {line}</p>
              ))}
            </div>
          )}

          {pendingResult.warnings.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 space-y-1">
              <p className="font-semibold">Things to double-check</p>
              {pendingResult.warnings.map((warning, idx) => (
                <p key={`warning-${idx}`}>&bull; {warning}</p>
              ))}
            </div>
          )}

          <button
            type="button"
            className="text-sm font-semibold text-primary-700 hover:text-primary-900"
            onClick={() => setShowPreview(prev => !prev)}
          >
            {showPreview ? 'Hide extracted text' : 'Show extracted text'}
          </button>
          {showPreview && (
            <pre className="max-h-60 overflow-y-auto whitespace-pre-wrap rounded-xl border border-secondary-200 bg-secondary-50 p-3 text-xs text-secondary-800">
              {pendingResult.extractedText}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

export default RubricUploadAssistant;
